import type { ParsedStatement, Transaction } from './types';
import { categorize } from './categorize';

/**
 * TymeBank / GoTyme parser. Handles both the PDF text-layer dump
 * (very similar to Capitec) and CSV exports from the GoTyme app.
 *
 * PDF format: Date | Description | Money In | Money Out | Balance
 *   - no Opening/Closing lines
 *   - description can be long and run over multiple lines; we join until the next date
 */

const MONEY = '[-+]?\\d{1,3}(?:[\\s ]?\\d{3})*(?:[.,]\\d{2})?';
const DATE  = '(\\d{2}\\/\\d{2}\\/\\d{4})';
const ROW_RE = new RegExp(
  `^\\s*${DATE}\\s+(.+?)\\s+(${MONEY})?\\s+(${MONEY})?\\s+(${MONEY})\\s*$`,
);

function parseMoney(raw: string | undefined): number | null {
  if (!raw) return null;
  let s = raw.replace(/\s/g, '').replace(/ /g, '');
  if (s.includes(',') && s.includes('.')) s = s.replace(/,/g, '');
  else if (s.includes(',')) s = s.replace(',', '.');
  const n = Number(s);
  return isFinite(n) ? n : null;
}

function normaliseDate(s: string): string {
  const [d, m, y] = s.split('/');
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

export function parseTymeBank(text: string): ParsedStatement {
  const warnings: string[] = [];
  const transactions: Transaction[] = [];
  const balanceMismatches: number[] = [];
  let prevBalance: number | null = null;

  // Try strict row-by-row; for descriptions that wrap, just take the date + tail
  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    if (!line.trim()) continue;
    const m = ROW_RE.exec(line);
    if (!m) continue;
    const [, date, desc, inStr, outStr, balStr] = m;
    const moneyIn  = parseMoney(inStr);
    const moneyOut = parseMoney(outStr);
    const balance  = parseMoney(balStr) ?? 0;
    if (moneyIn === null && moneyOut === null) continue;
    const signed = (moneyIn || 0) - (moneyOut || 0);
    if (prevBalance !== null) {
      const expected = Math.round((prevBalance + signed) * 100) / 100;
      if (Math.abs(expected - balance) > 0.01) balanceMismatches.push(transactions.length);
    }
    prevBalance = balance;
    transactions.push({
      date: normaliseDate(date),
      description: desc.trim().replace(/\s+/g, ' '),
      amount: Math.round(signed * 100) / 100,
      balance,
      category: categorize(desc),
    });
  }

  return {
    bank: 'tymebank',
    account: null,
    openingBalance: transactions[0]?.balance ?? null,
    closingBalance: transactions[transactions.length - 1]?.balance ?? null,
    transactions,
    warnings,
    balanceMismatches,
  };
}
